import { docusignRoles } from '@shared';

/**
 * Helpers for assigning a test email to each DocuSign signatory role before
 * sending a test envelope. The signatory roles come from the DocuSign template
 * itself (its signer role names); `docusignRoles` is the canonical
 * roleKey ↔ roleName map shared with the server.
 */

const ROLE_KEY_EMAIL_TAGS: Partial<Record<DocusignRoleKey, string>> = {
  applicationPreparer: 'preparer',
  advisor: 'advisor',
  primaryInsured: 'insured',
  secondaryInsured: 'insured2',
  policyOwner: 'owner',
  jointOwner: 'jointowner',
  policyOwnerSpouse: 'ownerspouse',
  irrevocableBeneficiary: 'beneficiary',
  agent2: 'agent2',
  agent3: 'agent3',
  residentAgent: 'residentagent',
  primaryInsuredSpouse: 'insuredspouse',
  legalRepresentative: 'legalrep',
  collateralAssignee: 'collateral',
  fieldPrincipal: 'fieldprincipal',
  homeOffice: 'homeoffice',
  policyOwnerAuthRepresentative1: 'authrep1',
  policyOwnerAuthRepresentative2: 'authrep2',
  policyOwnerAuthRepresentative3: 'authrep3',
  legalCounsel: 'legalcounsel',
  secretary: 'secretary',
  replacementPolicyAdvisor: 'replacementadvisor',
  captiveOfficer: 'captiveofficer',
  trustee1: 'trustee1',
  trustee2: 'trustee2',
  trustee3: 'trustee3',
  authorizedRepresentative: 'authrep',
  companyAdmin: 'companyadmin',
  authorizedMember: 'authmember',
  personalRepresentative: 'personalrep',
  policyOwnerPartnerOrMember1: 'partner1',
  policyOwnerPartnerOrMember2: 'partner2',
  policyOwnerPartnerOrMember3: 'partner3',
  policyOwnerPartnerOrMember4: 'partner4',
  applicantPartner: 'applicantpartner',
};

const ROLE_NAME_TO_KEY: Record<string, DocusignRoleKey> = Object.entries(docusignRoles).reduce(
  (acc, [roleKey, config]) => {
    acc[config.roleName] = roleKey as DocusignRoleKey;
    return acc;
  },
  {} as Record<string, DocusignRoleKey>,
);

/** Resolves a DocuSign template signer roleName to our internal role key. */
export function roleKeyFromRoleName(roleName: string): DocusignRoleKey | undefined {
  return ROLE_NAME_TO_KEY[roleName];
}

/** Human-readable label for a role key (the DocuSign template role name). */
export function roleKeyLabel(roleKey: DocusignRoleKey): string {
  return (
    docusignRoles[roleKey]?.roleName ??
    roleKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, c => c.toUpperCase())
      .trim()
  );
}

/**
 * Generates a plus-tagged email for a role, replacing any existing + suffix.
 * e.g. tagEmail('sajad@x.com', 'policyOwner') → 'sajad+owner@x.com'
 */
export function tagEmail(baseEmail: string, roleKey: DocusignRoleKey): string {
  const atIndex = baseEmail.indexOf('@');

  if (atIndex === -1) return baseEmail;

  const domain = baseEmail.slice(atIndex);
  const localPart = baseEmail.slice(0, atIndex);
  const plusIndex = localPart.indexOf('+');
  const cleanLocal = plusIndex === -1 ? localPart : localPart.slice(0, plusIndex);
  const tag = ROLE_KEY_EMAIL_TAGS[roleKey] ?? roleKey.toLowerCase();

  return `${cleanLocal}+${tag}${domain}`;
}

/**
 * Builds the default email assignment for every signatory role. The preparer is
 * always the advisor's own account email; every other role defaults to a tagged
 * variant so the envelope still works if the user leaves a field untouched.
 */
export function buildDefaultSignatoryEmails(
  roleKeys: DocusignRoleKey[],
  advisorEmail: string,
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const roleKey of roleKeys) {
    result[roleKey] =
      roleKey === 'applicationPreparer' ? advisorEmail : tagEmail(advisorEmail, roleKey);
  }

  return result;
}
